// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { rem } from 'polished';

// Components
import MyTeamChip from './my-team-chip';
import GracefulImage from '~/components/ui/layout/graceful-image/index';

const roundToOne = num => {
	return +(Math.round(num + 'e+1') + 'e-1');
};

/**********
 ** Component: MyTeamCard
 ** @type: functional stateless component
 ** @description: Photo of a team member
 **********/
const MyTeamCard = props => {
	const renderPhoto = (photo, name) => {
		if (photo) {
			return (
				<GracefulImage
					src={`${photo}`}
					width="138"
					height="138"
					alt={`This is ${name}`}
					placeholderColor="hsl(240, 9%, 98%)"
					noLazyLoad={true}
				/>
			);
		} else {
			return (
				<svg xmlns="http://www.w3.org/2000/svg" data-name="Layer 1" viewBox="0 0 138 138">
					<path
						fill="#fff"
						stroke="#b4bcc4"
						strokeDasharray="6 6"
						strokeWidth="2"
						d="M69 137A68 68 0 1 0 1 69a68 68 0 0 0 68 68z"
					/>
					<path
						fill="none"
						stroke="#b4bcc4"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2.5"
						d="M42.52 104.48a26.75 26.75 0 1 1 53.49 0M69.27 32.54A15.46 15.46 0 1 1 53.89 48a15.43 15.43 0 0 1 15.38-15.46z"
					/>
				</svg>
			);
		}
	};

	const renderName = name => {
		if (name) {
			return <h3 className="content__name">{name}</h3>;
		} else {
			return null;
		}
	};

	const renderDistance = (distance, isUser) => {
		if (distance !== null && distance !== undefined && isUser === true) {
			return <h4 className="content__distance">{`${roundToOne(distance / 1000)}Km`}</h4>;
		} else {
			return null;
		}
	};

	const renderDeleteButton = isCaptain => {
		if (isCaptain) {
			return <MyTeamChip isCaptain={props.isCaptain} isUser={props.isUser} isRequired={props.isRequired} />;
		} else {
			if (props.isCaptainSignedIn) {
				return (
					<MyTeamChip
						customLabel="Remove"
						isCaptain={props.isCaptain}
						isUser={props.isUser}
						isRequired={props.isRequired}
					/>
				);
			} else {
				return <MyTeamChip isCaptain={props.isCaptain} isUser={props.isUser} isRequired={props.isRequired} />;
			}
		}
	};

	return (
		<Wrapper className={`cell ${props.className}`} onClick={props.onClick}>
			<Center>
				<figure className="photo">{renderPhoto(props.photo, props.name)}</figure>
				<div className="content">
					{renderDeleteButton(props.isCaptain, props.isCaptainSignedIn)}
					<div className="content__main">
						{renderName(props.name)}
						{renderDistance(props.distance, props.isUser)}
					</div>
				</div>
			</Center>
		</Wrapper>
	);
};

const Wrapper = styled.li`
	display: flex;
	flex-direction: column;
	width: ${rem('96px')};
	margin-right: 0;
	margin-bottom: ${rem('24px')};

	.photo {
		width: ${rem('96px')};
		height: ${rem('96px')};
		border-radius: ${rem('96px')};
		margin-bottom: ${rem('12px')};
		background-color: ${props => props.theme.gray0};
		display: table;

		img,
		svg {
			display: flex;
			width: ${rem('96px')};
			height: ${rem('96px')};
			vertical-align: middle;
			display: table-cell;
			text-align: center;
		}

		img {
			border-radius: ${rem('96px')};
			background-color: ${props => props.theme.gray0};

			object-fit: cover;
			object-position: center center;
			color: ${props => props.theme.gray3};
		}
	}

	@media ${props => props.theme.breakpointLarge} {
		width: ${rem('138px')};

		.photo {
			width: ${rem('138px')};
			height: ${rem('138px')};
			border-radius: ${rem('138px')};

			img,
			svg {
				width: ${rem('138px')};
				height: ${rem('138px')};
			}

			img {
				border-radius: ${rem('138px')};
			}
		}
	}

	.content {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
		&__main {
			margin-top: ${rem('8px')};
			margin-bottom: 0;
			width: 100%;
		}
		&__name,
		&__distance {
			width: 100%;
			letter-spacing: 0;
			text-align: center;
			line-height: 1;
			margin-bottom: 0;
		}
		&__name {
			font-size: ${rem('18px')};
			font-weight: 600;
			color: ${props => props.theme.gray9};
			margin-bottom: ${rem('8px')};
		}
		&__distance {
			font-size: ${rem('14px')};
			color: ${props => props.theme.gray6};
			font-weight: 400;

			@media ${props => props.theme.breakpointLarge} {
				font-size: ${rem('16px')};
			}
		}
	}
`;

const Center = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	position: relative;
`;

MyTeamCard.defaultProps = {
	isCaptainSignedIn: false,
	className: `xlarge-2 large-3 small-6`,
};

MyTeamCard.propTypes = {
	id: PropTypes.number,
	isCaptainSignedIn: PropTypes.bool,
	photo: PropTypes.string,
	name: PropTypes.string,
	isCaptain: PropTypes.bool,
	isUser: PropTypes.bool,
	isRequired: PropTypes.bool,
	onClick: PropTypes.func,
	className: PropTypes.string,
};

export default MyTeamCard;



// WEBPACK FOOTER //
// ./src/pages/profile/my-team/my-team-card.jsx